import {Component, OnDestroy, OnInit} from '@angular/core';
import {HttpErrorResponse} from '@angular/common/http';
import {ActivatedRoute, ParamMap, Router} from '@angular/router';
import {ConfirmationService} from 'primeng/api';
import {Observable, Subscription} from 'rxjs';
import {distinctUntilChanged, filter, map} from 'rxjs/operators';
import {MvsCoreService} from '../../../shared/service/mvs-core.service';
import {PageComponent} from '../../../shared/mvs-page/page.component';
import {PageHeaderConfig} from '../../../shared/service/page/page-header-config.interface';
import {MvsMessageService} from '../../../shared/service/message/mvs-message.service';
import {DtoTemplate} from '../../../shared/dto/dto-template';
import {MvsFormDto} from '../../../shared/dto/form/dto/mvs-form.dto';
import {UiQlWidgetService} from '../../service/api/ui-ql-widget.service';
import {UiQlWidgetWorkbenchStateService} from '../../service/domain/ui-ql-widget-workbench-state.service';
import {UiQlWidgetWriteAccess} from '../../logic/ui-ql-widget-write-access';
import {UiQlWidgetWorkbenchMode} from '../../interface/ui-ql-widget-workbench-mode.interface';
import {UiQlWidgetWorkbenchAccess} from '../../interface/ui-ql-widget-workbench-access.interface';
import {UiQlWidgetWorkbenchLeftTab} from '../../interface/ui-ql-widget-workbench-left-tab.interface';
import {UiQlWidgetWorkbenchRightTab} from '../../interface/ui-ql-widget-workbench-right-tab.interface';
import {UiQlWidgetSaveDialogValue} from '../../interface/ui-ql-widget-save-dialog-value.interface';
import {UiQlWidgetViolationDto} from '../../model/dto/api/ui-ql-widget-violation.dto';
import {UiQlWidgetSaveResponseDto} from '../../model/dto/api/ui-ql-widget-save-response.dto';
import {UiQlWidgetSaveErrorBody} from '../../interface/ui-ql-widget-save-error-body.interface';
import {QlBuilderModel} from '../../../shared/ql/builder/ql-builder-model';
import {QlBuilderViolation} from '../../../shared/ql/builder/ql-builder-violation';
import {QlBuilderPresetResult} from '../../../shared/ql/builder/ql-builder-presets';
import {UiQlJsonDraft} from '../../interface/ui-ql-json-draft.interface';
import {UiQlWidgetPreviewState} from '../../interface/ui-ql-widget-preview-state.interface';
import {UiQlWidgetPreviewStatus} from '../../interface/ui-ql-widget-preview-status.interface';
import {UiQlWidgetWorkbenchState} from '../../interface/ui-ql-widget-workbench-state.interface';
import {UiQlWidgetMappingDefaults} from '../../logic/ui-ql-widget-mapping-defaults';
import {UiQlWorkbenchPreviewInput} from '../../interface/ui-ql-workbench-preview-input.interface';


/**
 * QL workbench — ONE page, TWO modes (requirement.md §Frontend, user-confirmed):
 *
 * | Route                          | `data.mode`  | Purpose                                    |
 * |--------------------------------|--------------|--------------------------------------------|
 * | `ui/widget-builder`            | `builder`    | new QL widget                              |
 * | `ui/widget-builder/:uiWidgetId`| `builder`    | edit (loads `UiQlWidgetService.getDefinition`) |
 * | `ui/ql-playground`             | `playground` | explore QL, no saving; "Als Widget speichern" |
 *
 * Layout: `mvs-review-workbench` (fe_core_review-workbench R1/R5) — LEFT = builder area
 * ("Builder | Chat"), RIGHT = preview (builder) or "Vorschau | QL-JSON | Inspektor" (playground).
 * This page is only the SHELL: every pane content is a clearly marked `ng-template` slot in the
 * HTML that the component/widget specialists replace. All of them share the per-page
 * {@link UiQlWidgetWorkbenchStateService} (listed in `providers`).
 *
 * Declared in `UiQlWidgetBuilderModule` (NgModule, fe_core_standards) and routed through the lazy
 * `UiQlWidgetBuilderRoutingModule` so `ui.module.ts` (owned by a parallel session) stays untouched.
 * NOT standalone: a standalone component that imports `SharedModule` gets its own environment
 * injector, which instantiates `BrowserModule` a second time (NG05100, white page).
 *
 * Access: ui write on `ui.UiWidget` (requirement.md business rule 4), read from the form actions
 * of a `ui.UiWidget` template — see {@link UiQlWidgetWriteAccess}. The backend enforces it anyway.
 */
@Component({
    selector: 'mvs-ui-ql-widget-workbench-page',
    templateUrl: './ui-ql-widget-workbench.page.html',
    styleUrls: ['./ui-ql-widget-workbench.page.scss'],
    standalone: false,
    providers: [UiQlWidgetWorkbenchStateService]
})
export class UiQlWidgetWorkbenchPage extends PageComponent implements OnInit, OnDestroy {

    static readonly ROUTE_BUILDER = '/ui/widget-builder';
    static readonly ROUTE_PLAYGROUND = '/ui/ql-playground';

    static readonly LABEL_BUILDER = 'Widget-Builder';
    static readonly LABEL_PLAYGROUND = 'QL-Playground';

    mode: UiQlWidgetWorkbenchMode = 'builder';

    access: UiQlWidgetWorkbenchAccess = 'checking';

    /** Edit route: `true` while the definition of `ui/widget-builder/:uiWidgetId` is being loaded. */
    loadingDefinition: boolean = false;
    loadError: string | null = null;

    /** Converter messages when a loaded definition cannot be represented by the builder. */
    unmappable: string[] = [];

    saveDialogVisible: boolean = false;
    saveDialogInitialValue: UiQlWidgetSaveDialogValue | null = null;
    saveBusy: boolean = false;
    saveViolations: UiQlWidgetViolationDto[] = [];
    saveErrorMessage: string | null = null;

    /** `true` while an activate/deactivate call of the loaded widget is running. */
    visibilityBusy: boolean = false;

    /** Model + server verdict for the preview; holds back while the verdict for a new revision is pending. */
    readonly previewInput$: Observable<UiQlWorkbenchPreviewInput>;

    /** Last facts reported by the preview — the inspector shows them (the preview stays mounted in every tab). */
    previewState: UiQlWidgetPreviewState | null = null;
    previewStatus: UiQlWidgetPreviewStatus | null = null;

    /** Raw JSON for the QL-JSON tab (negative preset: a shape the builder model cannot represent). */
    jsonDraft: UiQlJsonDraft | null = null;

    private readonly subscriptions = new Subscription();

    /** The widget id the current route asked for — loaded once access is granted. */
    private requestedUiWidgetId: number | null = null;

    constructor(
        protected override route: ActivatedRoute,
        protected override coreService: MvsCoreService,
        readonly state: UiQlWidgetWorkbenchStateService,
        private readonly qlWidgetService: UiQlWidgetService,
        private readonly messageService: MvsMessageService,
        private readonly router: Router,
        private readonly confirmationService: ConfirmationService) {
        super(route, coreService);

        this.previewInput$ = this.state.state$.pipe(
            map(s => UiQlWidgetWorkbenchPage.toPreviewInput(s)),
            filter((input): input is UiQlWorkbenchPreviewInput => input !== undefined),
            distinctUntilChanged((a, b) => a.model === b.model && a.policyViolations === b.policyViolations
                && a.widgetType === b.widgetType && a.mapping === b.mapping),
        );
    }

    override ngOnInit(): void {

        this.mode = UiQlWidgetWorkbenchPage.modeFromRoute(this.route);
        this.defaultLabel = UiQlWidgetWorkbenchPage.labelFor(this.mode);

        super.ngOnInit();

        // a playground instance may have stashed its content for this builder instance (N3)
        this.state.init(this.mode);
        this.state.startAutoValidation();

        this.subscriptions.add(
            this.state.mode$.subscribe(mode => this.onModeChanged(mode))
        );

        this.subscriptions.add(
            this.state.accessDenied$.subscribe(() => this.access = 'denied')
        );

        // STREAM, not snapshot: Angular reuses this instance between 'ui/widget-builder/5' and '/7'.
        this.subscriptions.add(
            this.route.paramMap.subscribe(params => this.onRouteParams(params))
        );

        this.checkAccess();
    }

    override ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
        super.ngOnDestroy();
    }

    /** Template mode: the header binds to this page's live state (mode, dirty, canSave). */
    override getPageHeaderConfig(): PageHeaderConfig {
        return {
            templateRef: this.headerTemplate,
            sticky: true,
        };
    }

    // ------------------------------------------------------------------
    // header actions
    // ------------------------------------------------------------------

    /** "Speichern" (builder mode). */
    openSaveDialog(): void {

        if (this.mode !== 'builder' || !this.state.canSave()) {
            return;
        }

        const meta = this.state.snapshot.meta;

        this.saveDialogInitialValue = {
            name: meta.name,
            description: meta.description,
            sizes: [...meta.sizes],
            defaultSize: meta.defaultSize,
        };
        this.saveViolations = [];
        this.saveErrorMessage = null;
        this.saveDialogVisible = true;
    }

    /**
     * "Als Widget speichern" (playground mode): a REAL router navigation to the builder route (N3). A bare
     * `location.replaceState` left the router on the playground URL, so a later navigation to
     * `#/ui/widget-builder` or back to the playground was ignored as "same URL". The router rebuilds the
     * page for the new route config; the content (model, type, mapping, chat) travels through
     * {@link UiQlWidgetWorkbenchStateService.stashHandover} into the new instance's `init('builder')`.
     *
     * A PUSHED navigation (Q3-5): with `replaceUrl` the playground entry was overwritten, so browser "back"
     * from the builder left the app (about:blank). The handover is taken on read, so going back and forth
     * never applies it twice. (Only the save → `/:id` step replaces the URL - that is the same draft.)
     */
    switchToBuilder(): void {

        if (this.mode !== 'playground') {
            return;
        }

        this.state.stashHandover();
        this.router.navigate([UiQlWidgetWorkbenchPage.ROUTE_BUILDER]);
    }

    /** "Zurücksetzen" - with unsaved changes only after confirmation (QA #22); the global `p-confirmDialog` renders it. */
    resetWorkbench(): void {

        if (!this.state.snapshot.dirty) {
            this.doReset();
            return;
        }

        this.confirmationService.confirm({
            header: 'Zurücksetzen',
            message: this.mode === 'builder' && this.state.snapshot.meta.uiWidgetId != null
                ? 'Ungespeicherte Änderungen gehen verloren – das Widget wird auf den gespeicherten Stand zurückgesetzt. Fortfahren?'
                : 'Ungespeicherte Änderungen gehen verloren – die Abfrage wird geleert. Fortfahren?',
            icon: 'fa-regular fa-triangle-exclamation',
            acceptLabel: 'Zurücksetzen',
            rejectLabel: 'Abbrechen',
            acceptButtonStyleClass: 'p-button-danger',
            rejectButtonStyleClass: 'p-button-text',
            accept: () => this.doReset(),
        });
    }

    /** "Aktivieren" - a deactivated saved widget is offered again; no confirmation (nothing is lost). */
    activateWidget(): void {

        const meta = this.state.snapshot.meta;

        if (this.mode !== 'builder' || meta.uiWidgetId == null || !meta.hidden || this.visibilityBusy) {
            return;
        }

        this.changeVisibility(meta.uiWidgetId, false);
    }

    /** "Deaktivieren" - only after confirmation; placed tiles keep working, the library stops offering it. */
    deactivateWidget(): void {

        const meta = this.state.snapshot.meta;

        if (this.mode !== 'builder' || meta.uiWidgetId == null || meta.hidden || this.visibilityBusy) {
            return;
        }

        const uiWidgetId = meta.uiWidgetId;

        this.confirmationService.confirm({
            header: 'Widget deaktivieren',
            message: `„${meta.name}" wird nicht mehr zum Hinzufügen angeboten. Bereits platzierte Kacheln bleiben erhalten. Fortfahren?`,
            icon: 'fa-regular fa-triangle-exclamation',
            acceptLabel: 'Deaktivieren',
            rejectLabel: 'Abbrechen',
            acceptButtonStyleClass: 'p-button-danger',
            rejectButtonStyleClass: 'p-button-text',
            accept: () => this.changeVisibility(uiWidgetId, true),
        });
    }

    private changeVisibility(uiWidgetId: number, hidden: boolean): void {

        const call$ = hidden
            ? this.qlWidgetService.deactivateDefinition(uiWidgetId)
            : this.qlWidgetService.activateDefinition(uiWidgetId);

        this.visibilityBusy = true;

        this.subscriptions.add(
            call$.subscribe({
                next: () => {
                    this.visibilityBusy = false;
                    // the user may have navigated to another widget meanwhile - only the same one takes the flag
                    if (this.state.snapshot.meta.uiWidgetId === uiWidgetId) {
                        this.state.markHidden(hidden);
                    }
                    this.messageService.showSuccessMessage(
                        hidden ? 'Widget deaktiviert' : 'Widget aktiviert',
                        hidden
                            ? 'Das Widget wird nicht mehr zum Hinzufügen angeboten.'
                            : 'Das Widget wird wieder zum Hinzufügen angeboten.');
                },
                error: error => {
                    this.visibilityBusy = false;
                    this.messageService.showErrorMessage(
                        hidden ? 'Deaktivieren fehlgeschlagen' : 'Aktivieren fehlgeschlagen',
                        UiQlWidgetWorkbenchPage.visibilityErrorText(error, hidden));
                },
            })
        );
    }

    /** 403 = no ui write, 404 = widget gone; anything else a generic text. */
    static visibilityErrorText(error: unknown, hidden: boolean): string {

        const status = (error && typeof error === 'object') ? (error as Partial<HttpErrorResponse>).status : undefined;

        if (UiQlWidgetWriteAccess.isForbidden(error)) {
            return hidden
                ? 'Keine Berechtigung zum Deaktivieren von Widgets.'
                : 'Keine Berechtigung zum Aktivieren von Widgets.';
        }
        if (status === 404) {
            return 'Das Widget wurde nicht gefunden.';
        }
        return hidden
            ? 'Das Widget konnte nicht deaktiviert werden.'
            : 'Das Widget konnte nicht aktiviert werden.';
    }

    private doReset(): void {
        this.unmappable = [];
        this.state.reset();
    }

    setLeftTab(tab: UiQlWidgetWorkbenchLeftTab): void {
        this.state.setLeftTab(tab);
    }

    setRightTab(tab: UiQlWidgetWorkbenchRightTab): void {
        this.state.setRightTab(tab);
    }

    // ------------------------------------------------------------------
    // builder, presets, JSON, preview
    // ------------------------------------------------------------------

    onModelChange(model: QlBuilderModel | null): void {
        this.state.setModel(model);
    }

    /**
     * A preset from the playground dropdown. With a model: load model, widget type and a default
     * mapping. Without (negative preset #8, not representable by the builder): clear the builder so
     * nothing runs, and open the raw JSON in the QL-JSON tab where its violations are shown.
     */
    onPresetLoad(preset: QlBuilderPresetResult): void {

        if (preset.status !== 'available') {
            return;
        }

        if (preset.model) {
            this.state.setModel(preset.model);
            this.state.setWidgetType(preset.widgetTypeHint);
            this.state.setMapping(UiQlWidgetMappingDefaults.forType(preset.widgetTypeHint, preset.model));
            // Q3-4: the JSON tab was only opened for a preset the builder cannot represent - a representable
            // one shows its result again (another tab the user chose, e.g. the inspector, is kept)
            if (this.state.snapshot.rightTab === 'json') {
                this.state.setRightTab('preview');
            }
            return;
        }

        this.state.setModel(null);
        this.jsonDraft = {text: JSON.stringify(preset.qlQuery, null, 2), seq: (this.jsonDraft?.seq ?? 0) + 1};
        this.state.setRightTab('json');
    }

    onJsonApply(model: QlBuilderModel): void {
        this.state.setModel(model);
    }

    onPreviewStateChange(previewState: UiQlWidgetPreviewState): void {
        this.previewState = previewState;
        this.previewStatus = null;
    }

    onPreviewStatusChange(previewStatus: UiQlWidgetPreviewStatus): void {
        this.previewStatus = previewStatus;
    }

    /**
     * `undefined` = hold the previous preview input (the verdict for the current revision is still
     * pending — running the new model now would execute a definition the policy may still reject).
     * A technically failed validate call does not block: the client pre-check inside the preview
     * still guards, and the engine enforces the viewer's rights on `/core/ql/query` regardless.
     *
     * Model, widget type and mapping are returned TOGETHER (QA #8): binding type/mapping straight from
     * the state while the model is held gave the preview "old model + new type" for one debounce
     * window, i.e. one query of the previous definition on every preset change.
     */
    static toPreviewInput(s: UiQlWidgetWorkbenchState): UiQlWorkbenchPreviewInput | undefined {

        const snapshot = (policyViolations: QlBuilderViolation[] | null): UiQlWorkbenchPreviewInput => ({
            model: s.model,
            widgetType: s.widgetType ?? 'table',
            mapping: s.mapping ?? {},
            policyViolations,
        });

        if (!s.model) {
            return snapshot(null);
        }

        const current = s.validation && s.validation.revision === s.revision ? s.validation.result : null;

        if (current) {
            return snapshot(current.valid ? null : (current.violations ?? []));
        }
        if (s.validationError || !s.validating) {
            return snapshot(null);
        }
        return undefined;
    }

    // ------------------------------------------------------------------
    // save
    // ------------------------------------------------------------------

    onSaveDialogSubmit(value: UiQlWidgetSaveDialogValue): void {

        const request = this.state.buildSaveRequest(value);

        if (!request) {
            this.saveErrorMessage = 'Die Definition ist unvollständig und kann nicht gespeichert werden.';
            return;
        }

        const uiWidgetId = this.state.snapshot.meta.uiWidgetId;
        const call$ = uiWidgetId != null
            ? this.qlWidgetService.updateDefinition(uiWidgetId, request)
            : this.qlWidgetService.createDefinition(request);

        this.saveBusy = true;
        this.saveViolations = [];
        this.saveErrorMessage = null;

        this.subscriptions.add(
            call$.subscribe({
                next: response => this.onSaveSuccess(response, value, uiWidgetId == null),
                error: error => this.onSaveError(error),
            })
        );
    }

    private onSaveSuccess(response: UiQlWidgetSaveResponseDto, value: UiQlWidgetSaveDialogValue, created: boolean): void {

        this.saveBusy = false;

        if (response?.violations?.length && response.uiWidgetId == null) {
            // defensive: a 200 carrying violations but no id is a refusal, not a save
            this.saveViolations = response.violations;
            return;
        }

        this.state.markSaved(response, value);
        this.saveDialogVisible = false;

        if (created && response?.uiWidgetId != null) {
            // new widget -> edit URL through the ROUTER (N3): `location.replaceState` hid the id from the router,
            // so a later navigation to `#/ui/widget-builder` was ignored and the next save was a PUT on this widget
            this.router.navigate([UiQlWidgetWorkbenchPage.ROUTE_BUILDER, response.uiWidgetId], {replaceUrl: true});
        }

        this.messageService.showSuccessMessage(
            'Widget gespeichert',
            response?.envBound
                ? `„${value.name}" wurde gespeichert (umgebungsgebunden).`
                : `„${value.name}" wurde gespeichert.`);
    }

    /**
     * 400 = policy violations (`violations` in the body), 409 = alias exists (`aliasExists` violation, or a
     * bare 409 from an older backend), 401/403 = no ui write.
     * The texts stay in the dialog so the user can correct and retry without losing input.
     */
    private onSaveError(error: HttpErrorResponse | unknown): void {

        this.saveBusy = false;

        // read structurally, not via instanceof: interceptors / test doubles may hand over a plain {status, error}
        const httpError = (error && typeof error === 'object') ? error as Partial<HttpErrorResponse> : null;
        const status = httpError?.status;
        const body: unknown = httpError?.error;
        const bodyObject = UiQlWidgetWorkbenchPage.asSaveErrorBody(body);

        // 400 = policy violations; 409 = `aliasExists` (path `alias`) - both carry `violations`, the dialog
        // translates the code (`uiQlViolationText`), so the user sees "Dieser Alias ist bereits vergeben."
        if ((status === 400 || status === 409) && bodyObject?.violations?.length) {
            this.saveViolations = bodyObject.violations;
            this.saveErrorMessage = 'Das Widget wurde nicht gespeichert.';
            return;
        }

        if (status === 409) {
            // older backend without the violation body
            this.saveErrorMessage = 'Ein Widget mit diesem Alias existiert bereits. Bitte einen anderen Namen wählen.';
            return;
        }

        if (UiQlWidgetWriteAccess.isForbidden(error)) {
            this.access = 'denied';
            this.saveErrorMessage = 'Keine Berechtigung zum Speichern von Widgets.';
            return;
        }

        const serverMessage = typeof body === 'string' ? body : (bodyObject?.message || bodyObject?.error);
        this.saveErrorMessage = serverMessage
            ? `Das Widget konnte nicht gespeichert werden: ${serverMessage}`
            : 'Das Widget konnte nicht gespeichert werden.';
    }

    /** Narrows an unknown HTTP error body to the fields the save error handling reads; `null` if it is no object. */
    private static asSaveErrorBody(body: unknown): UiQlWidgetSaveErrorBody | null {

        if (!body || typeof body !== 'object') {
            return null;
        }

        const candidate = body as { violations?: unknown; message?: unknown; error?: unknown };
        return {
            violations: Array.isArray(candidate.violations) ? candidate.violations as UiQlWidgetViolationDto[] : undefined,
            message: typeof candidate.message === 'string' ? candidate.message : undefined,
            error: typeof candidate.error === 'string' ? candidate.error : undefined,
        };
    }

    // ------------------------------------------------------------------
    // access + loading
    // ------------------------------------------------------------------

    private checkAccess(): void {

        this.access = 'checking';

        this.subscriptions.add(
            this.coreService.getCrudService('ui.UiWidget').template(DtoTemplate.createEmpty()).subscribe({
                next: (form: MvsFormDto) => this.onAccessResolved(UiQlWidgetWriteAccess.fromForm(form)),
                error: error => this.onAccessResolved(UiQlWidgetWriteAccess.fromError(error)),
            })
        );
    }

    private onAccessResolved(access: UiQlWidgetWorkbenchAccess): void {
        this.access = access;
        if (access === 'granted') {
            this.loadRequestedDefinition();
        }
    }

    private onRouteParams(params: ParamMap): void {

        const raw = params.get('uiWidgetId');
        const id = raw != null && raw !== '' ? Number(raw) : null;

        this.requestedUiWidgetId = id != null && Number.isFinite(id) && id > 0 ? id : null;
        this.loadError = raw != null && this.requestedUiWidgetId === null ? 'Ungültige Widget-ID.' : null;

        // "Neu" while a saved widget is loaded: the route says "no widget" -> the state must not keep the old
        // one, or the next save would be a PUT on it (N3). An unsaved draft (no id - e.g. the playground
        // handover) is kept.
        if (this.requestedUiWidgetId === null && this.mode === 'builder' && this.state.snapshot.meta.uiWidgetId != null) {
            this.unmappable = [];
            this.state.init('builder');
        }

        if (this.access === 'granted') {
            this.loadRequestedDefinition();
        }
    }

    private loadRequestedDefinition(): void {

        const id = this.requestedUiWidgetId;

        if (id == null || this.mode !== 'builder' || this.state.snapshot.meta.uiWidgetId === id) {
            return;
        }

        this.loadingDefinition = true;
        this.loadError = null;
        this.unmappable = [];

        this.subscriptions.add(
            this.qlWidgetService.getDefinition(id).subscribe({
                next: dto => {
                    this.loadingDefinition = false;
                    this.unmappable = this.state.applyDefinition(dto);
                },
                error: error => {
                    this.loadingDefinition = false;
                    if (UiQlWidgetWriteAccess.isForbidden(error)) {
                        this.access = 'denied';
                    } else if (error?.status === 404) {
                        this.loadError = 'Das Widget wurde nicht gefunden.';
                    } else {
                        this.loadError = 'Das Widget konnte nicht geladen werden.';
                    }
                }
            })
        );
    }

    private onModeChanged(mode: UiQlWidgetWorkbenchMode): void {

        if (mode === this.mode) {
            return;
        }

        this.mode = mode;
        this.defaultLabel = UiQlWidgetWorkbenchPage.labelFor(mode);
        this.setActiveTitle();
    }

    private static modeFromRoute(route: ActivatedRoute): UiQlWidgetWorkbenchMode {
        return route?.snapshot?.data?.['mode'] === 'playground' ? 'playground' : 'builder';
    }

    private static labelFor(mode: UiQlWidgetWorkbenchMode): string {
        return mode === 'playground'
            ? UiQlWidgetWorkbenchPage.LABEL_PLAYGROUND
            : UiQlWidgetWorkbenchPage.LABEL_BUILDER;
    }
}
